import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DocumentArrowDownIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Text } from '../../components/ui'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import { estoqueService } from '../../services/estoque'
import { marketingSolicitacaoService } from '../../services/marketingSolicitacao'
import { marketingParametersService } from '../../services/marketingParameters'
import { marketingNotasService } from '../../services/marketingNotas'
import { paymentSourcesService } from '../../services/paymentSources'
import {
  CONTRACT_ATTACHMENT_ACCEPT,
  contratosService,
  getOpenAmountsTotal,
} from '../../services/contratos'
import type {
  Contract,
  ContractAttachment,
  ContractInput,
  ContractRenewalType,
} from '../../types/contratos'
import type { EstoqueFornecedor, PaymentMethod } from '../../types/estoque'
import type { MarketingCampaign, MarketingEvent } from '../../types/marketingNotas'
import type { PaymentSource } from '../../types/paymentSources'
import type { MarketingRequest } from '../../types/marketingSolicitacao'
import EstoqueFornecedorCreateModal from '../Estoque/EstoqueFornecedorCreateModal'
import SolicitacaoPickerField from './SolicitacaoPickerField'
import NotasPaymentStep from './NotasPaymentStep'
import {
  ICON_BTN_CLASS,
  ICON_BTN_DANGER_CLASS,
  SELECT_CLASS,
  TEXTAREA_CLASS,
  emptyPaymentGroup,
  formatCurrency,
  formatDateOnlyPtBr,
  isConfirmCancelled,
  paymentGroupsTotal,
  paymentsFromInvoiceRows,
  toPaymentGroupInputs,
  type PaymentDraftGroup,
} from './notasUi'

type Step = 'form' | 'payment' | 'review'

type TimelineStep = {
  number: number
  label: string
  description: string
}

type ServiceDraft = {
  amount: string
  effective_from: string
  effective_to: string
  department_id: string
  notes: string
}

type Props = {
  open: boolean
  editing: Contract | null
  busy?: boolean
  onClose: () => void
  onSave: (input: ContractInput, pendingFiles: File[]) => Promise<Contract>
  onConclude: (contract: Contract) => Promise<void>
  onEditingChange?: (contract: Contract) => void
}

const TIMELINE_STEPS: TimelineStep[] = [
  {
    number: 1,
    label: 'Dados do contrato',
    description: 'Cabeçalho, solicitação, serviços e anexos',
  },
  {
    number: 2,
    label: 'Forma de pagamento',
    description: 'Parcelas e vencimentos do contrato',
  },
  {
    number: 3,
    label: 'Revisão',
    description: 'Salvar ou concluir o contrato',
  },
]

const RENEWAL_LABELS: Record<ContractRenewalType, string> = {
  recorrente: 'Recorrente',
  aditivo: 'Renovação',
}

function ContractTimeline({
  steps,
  currentStep,
}: {
  steps: TimelineStep[]
  currentStep: number
}) {
  return (
    <ol className="flex items-start gap-0 overflow-x-auto pb-1">
      {steps.map((item, index) => {
        const isActive = item.number === currentStep
        const isDone = item.number < currentStep
        const isLast = index === steps.length - 1
        return (
          <li
            key={item.number}
            className="relative flex min-w-[9.5rem] flex-1 flex-col items-center px-1 text-center"
          >
            <div className="relative mb-2 flex w-full items-center justify-center">
              {!isLast && (
                <span
                  className={`absolute left-1/2 top-1/2 z-0 h-0.5 w-full ${
                    isDone ? 'bg-primary-400' : 'bg-gray-200 dark:bg-gray-600'
                  }`}
                  aria-hidden
                />
              )}
              <span
                className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                  isActive
                    ? 'bg-primary-600 text-white ring-4 ring-primary-100 dark:ring-primary-900/40'
                    : isDone
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-200'
                      : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {item.number}
              </span>
            </div>
            <p
              className={`text-sm font-medium leading-snug ${
                isActive
                  ? 'text-primary-700 dark:text-primary-300'
                  : isDone
                    ? 'text-gray-800 dark:text-gray-100'
                    : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {item.label}
            </p>
            <p className="mt-0.5 text-xs leading-snug text-gray-500 dark:text-gray-400">
              {item.description}
            </p>
          </li>
        )
      })}
    </ol>
  )
}

const emptyService = (): ServiceDraft => ({
  amount: '',
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: '',
  department_id: '',
  notes: '',
})

const ContratosFormModal: React.FC<Props> = ({
  open,
  editing,
  busy = false,
  onClose,
  onSave,
  onConclude,
  onEditingChange,
}) => {
  const { user } = useAuthContext()
  const { logDelete } = useUserLogs()
  const [step, setStep] = useState<Step>('form')
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [contractNumber, setContractNumber] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [description, setDescription] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [paymentSourceId, setPaymentSourceId] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [responsibleId, setResponsibleId] = useState('')
  const [campaignId, setCampaignId] = useState('')
  const [eventId, setEventId] = useState('')
  const [adhesionDate, setAdhesionDate] = useState('')
  const [renewalType, setRenewalType] = useState<'' | ContractRenewalType>('')
  const [terminationDate, setTerminationDate] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [requestId, setRequestId] = useState('')
  const [services, setServices] = useState<ServiceDraft[]>([emptyService()])
  const [paymentGroups, setPaymentGroups] = useState<PaymentDraftGroup[]>([
    emptyPaymentGroup(0),
  ])
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [savedAttachments, setSavedAttachments] = useState<ContractAttachment[]>([])

  const [fornecedores, setFornecedores] = useState<EstoqueFornecedor[]>([])
  const [fontesPagadoras, setFontesPagadoras] = useState<PaymentSource[]>([])
  const [departments, setDepartments] = useState<
    { id: string; name: string; display_name: string }[]
  >([])
  const [profiles, setProfiles] = useState<{ id: string; name: string | null }[]>([])
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([])
  const [events, setEvents] = useState<MarketingEvent[]>([])
  const [marketingDepartmentConfigured, setMarketingDepartmentConfigured] = useState(true)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [requests, setRequests] = useState<MarketingRequest[]>([])
  const [loadingMeta, setLoadingMeta] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fornecedorCreateOpen, setFornecedorCreateOpen] = useState(false)
  const suppressReinitRef = useRef(false)

  const editingId = editing?.id ?? null

  const responsibleOptions = useMemo(() => {
    const list = [...profiles]
    if (editing?.responsible && !list.some((p) => p.id === editing.responsible!.id)) {
      list.unshift(editing.responsible)
    }
    return list
  }, [profiles, editing?.responsible])

  const activeCampaigns = useMemo(
    () => campaigns.filter((c) => c.is_active),
    [campaigns]
  )
  const activeEvents = useMemo(() => events.filter((e) => e.is_active), [events])

  useEffect(() => {
    if (!open) {
      // Só libera o skip ao fechar o modal (sobrevive ao double-invoke do Strict Mode).
      suppressReinitRef.current = false
      return
    }
    // Após o 1º save na revisão, o parent promove editing null→id; não resetar o formulário.
    if (suppressReinitRef.current) return

    let cancelled = false
    const contractSnapshot = editing

    const load = async () => {
      setLoadingMeta(true)
      setError(null)
      try {
        const [
          fornecedoresData,
          fontesData,
          departmentsData,
          requestsData,
          methodsData,
          profilesPicker,
          campaignsData,
          eventsData,
        ] = await Promise.all([
          estoqueService.listFornecedores(false),
          paymentSourcesService.list(true),
          marketingParametersService.listDepartmentsActive(),
          marketingSolicitacaoService.listRequests(),
          estoqueService.listFormasPagamento(true),
          marketingSolicitacaoService.listActiveProfilesForPicker(),
          marketingNotasService.listCampaigns(true),
          marketingNotasService.listEvents(true),
        ])
        if (cancelled) return

        setFornecedores(fornecedoresData.filter((f) => f.is_active))
        setDepartments(departmentsData)
        setRequests(requestsData)
        setMarketingDepartmentConfigured(Boolean(profilesPicker.departmentId))
        setProfiles(profilesPicker.profiles)
        setCampaigns(campaignsData)
        setEvents(eventsData)

        if (contractSnapshot) {
          setStep('form')
          setName(contractSnapshot.name || '')
          setContractNumber(contractSnapshot.contract_number || '')
          setContactName(contractSnapshot.contact_name || '')
          setContactPhone(contractSnapshot.contact_phone || '')
          setContactEmail(contractSnapshot.contact_email || '')
          setDescription(contractSnapshot.description || '')
          setSupplierId(contractSnapshot.supplier_id || '')
          setPaymentSourceId(contractSnapshot.payment_source_id || '')
          const usedFonteIds = new Set(
            contractSnapshot.payment_source_id ? [contractSnapshot.payment_source_id] : []
          )
          setFontesPagadoras(
            fontesData.filter((f) => f.is_active || usedFonteIds.has(f.id))
          )
          setDepartmentId(contractSnapshot.department_id || '')
          setResponsibleId(contractSnapshot.responsible_id || '')
          setCampaignId(contractSnapshot.campaign_id || '')
          setEventId(contractSnapshot.event_id || '')
          setAdhesionDate(contractSnapshot.adhesion_date?.slice(0, 10) || '')
          setRenewalType(contractSnapshot.renewal_type || '')
          setTerminationDate(contractSnapshot.termination_date?.slice(0, 10) || '')
          setIsActive(contractSnapshot.is_active)
          setRequestId(contractSnapshot.request_id || '')
          setSavedAttachments(contractSnapshot.attachments || [])

          const openAmounts = (contractSnapshot.amounts || []).filter((a) => !a.effective_to)
          const sourceAmounts = openAmounts.length
            ? openAmounts
            : contractSnapshot.amounts || []
          setServices(
            sourceAmounts.length
              ? sourceAmounts.map((a) => ({
                  amount: String(a.amount),
                  effective_from: a.effective_from.slice(0, 10),
                  effective_to: a.effective_to?.slice(0, 10) || '',
                  department_id: a.department_id || '',
                  notes: a.notes || '',
                }))
              : [emptyService()]
          )

          const loadedPayments = paymentsFromInvoiceRows(contractSnapshot.payments, methodsData)
          const usedMethodIds = new Set(
            (contractSnapshot.payments || []).map((p) => p.payment_method_id)
          )
          setPaymentMethods(
            methodsData.filter((m) => m.is_active || usedMethodIds.has(m.id))
          )
          const total = getOpenAmountsTotal(contractSnapshot.amounts)
          setPaymentGroups(
            loadedPayments.length
              ? loadedPayments
              : [emptyPaymentGroup(total, contractSnapshot.adhesion_date || undefined)]
          )
          setPendingFiles([])
        } else {
          setFontesPagadoras(fontesData.filter((f) => f.is_active))
          setStep('form')
          setName('')
          setContractNumber('')
          setContactName('')
          setContactPhone('')
          setContactEmail('')
          setDescription('')
          setSupplierId('')
          setPaymentSourceId('')
          setDepartmentId('')
          setResponsibleId('')
          setCampaignId('')
          setEventId('')
          setAdhesionDate('')
          setRenewalType('')
          setTerminationDate('')
          setIsActive(true)
          setRequestId('')
          setServices([emptyService()])
          setPaymentGroups([emptyPaymentGroup(0)])
          setPendingFiles([])
          setSavedAttachments([])
          setPaymentMethods(methodsData.filter((m) => m.is_active))
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar dados do formulário.')
        }
      } finally {
        if (!cancelled) setLoadingMeta(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
    // Só reinicializa ao abrir o modal ou trocar o contrato (não ao atualizar anexos).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- editing intencional via editingId
  }, [open, editingId])

  const currentTimelineStep = step === 'review' ? 3 : step === 'payment' ? 2 : 1

  const contractTotal = useMemo(() => {
    const total = services.reduce((acc, row) => {
      const amount = Number(String(row.amount).replace(',', '.'))
      return acc + (Number.isFinite(amount) && amount > 0 ? amount : 0)
    }, 0)
    return Math.round(total * 10000) / 10000
  }, [services])

  const firstServiceFrom = services[0]?.effective_from || ''

  const supplierName =
    fornecedores.find((f) => f.id === supplierId)?.name ||
    editing?.supplier?.name ||
    '—'
  const paymentSourceName =
    fontesPagadoras.find((f) => f.id === paymentSourceId)?.name ||
    editing?.payment_source?.name ||
    '—'
  const departmentName =
    departments.find((d) => d.id === departmentId)?.display_name ||
    departments.find((d) => d.id === departmentId)?.name ||
    '—'
  const responsibleName =
    responsibleOptions.find((p) => p.id === responsibleId)?.name || '—'
  const campaignName = campaigns.find((c) => c.id === campaignId)?.name || '—'
  const eventName = events.find((e) => e.id === eventId)?.name || '—'
  const requestLabel = (() => {
    const req = requests.find((r) => r.id === requestId)
    if (!req) return requestId ? 'Vinculada' : 'Não vinculada'
    return `#${req.request_number}`
  })()

  const updateService = (index: number, patch: Partial<ServiceDraft>) => {
    setServices((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const addService = () => {
    setServices((prev) => [...prev, emptyService()])
  }

  const removeService = (index: number) => {
    setServices((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)))
  }

  const validateHeader = (): string | null => {
    if (!name.trim()) return 'Título é obrigatório.'
    if (!supplierId) return 'Fornecedor é obrigatório.'
    if (!paymentSourceId) return 'Fonte pagadora é obrigatória.'
    if (renewalType === 'recorrente' && !adhesionDate) {
      return 'Informe a data de adesão para contratos recorrentes.'
    }
    if (!services.length) return 'Informe ao menos um serviço.'
    for (let i = 0; i < services.length; i++) {
      const row = services[i]
      const amount = Number(String(row.amount).replace(',', '.'))
      if (!Number.isFinite(amount) || amount <= 0) {
        return services.length === 1
          ? 'Informe um valor maior que zero.'
          : `Informe um valor maior que zero no serviço ${i + 1}.`
      }
      if (!row.effective_from) {
        return `Informe o início da vigência no serviço ${i + 1}.`
      }
    }
    return null
  }

  const validatePayments = (): string | null => {
    if (!paymentGroups.length) return 'Informe ao menos uma forma de pagamento.'
    for (let g = 0; g < paymentGroups.length; g++) {
      const group = paymentGroups[g]
      if (!group.payment_method_id) {
        return `Selecione a forma de pagamento no grupo ${g + 1}.`
      }
      if (!group.installments.length) {
        return `Informe ao menos uma parcela no grupo ${g + 1}.`
      }
      for (let i = 0; i < group.installments.length; i++) {
        const row = group.installments[i]
        if (!Number.isFinite(Number(row.amount)) || Number(row.amount) <= 0) {
          return `Valor inválido na parcela ${i + 1} do grupo ${g + 1}.`
        }
        if (!row.due_date) {
          return `Informe o vencimento da parcela ${i + 1} do grupo ${g + 1}.`
        }
      }
    }
    const allocated = paymentGroupsTotal(paymentGroups)
    if (Math.abs(allocated - contractTotal) > 0.009) {
      return `A soma dos pagamentos (${allocated.toFixed(2)}) deve ser igual ao total do contrato (${contractTotal.toFixed(2)}).`
    }
    return null
  }

  const goToPayment = () => {
    setError(null)
    const headerError = validateHeader()
    if (headerError) {
      setError(headerError)
      return
    }

    const allocated = paymentGroupsTotal(paymentGroups)
    if (Math.abs(allocated - contractTotal) > 0.009 || !paymentGroups[0]?.payment_method_id) {
      setPaymentGroups([emptyPaymentGroup(contractTotal, adhesionDate || undefined)])
    }
    setStep('payment')
  }

  const goToReview = () => {
    setError(null)
    const headerError = validateHeader()
    if (headerError) {
      setError(headerError)
      setStep('form')
      return
    }
    const paymentError = validatePayments()
    if (paymentError) {
      setError(paymentError)
      return
    }
    setStep('review')
  }

  const buildInput = (): ContractInput => ({
    name,
    description,
    contract_number: contractNumber || null,
    contact_name: contactName || null,
    contact_phone: contactPhone || null,
    contact_email: contactEmail || null,
    supplier_id: supplierId,
    payment_source_id: paymentSourceId,
    department_id: departmentId || null,
    responsible_id: responsibleId || null,
    campaign_id: campaignId || null,
    event_id: eventId || null,
    adhesion_date: adhesionDate || null,
    renewal_type: renewalType || null,
    termination_date: terminationDate || null,
    is_active: isActive,
    request_id: requestId || null,
    amounts: services.map((row) => ({
      amount: Number(String(row.amount).replace(',', '.')),
      effective_from: row.effective_from,
      effective_to: row.effective_to || null,
      department_id: row.department_id || null,
      notes: row.notes || null,
    })),
    payments: toPaymentGroupInputs(paymentGroups),
  })

  const persistDraft = async (): Promise<Contract> => {
    suppressReinitRef.current = true
    const saved = await onSave(buildInput(), pendingFiles)
    setPendingFiles([])
    if (saved.attachments) setSavedAttachments(saved.attachments)
    return saved
  }

  const errorMessage = (err: unknown, fallback: string) => {
    if (err instanceof Error && err.message.trim()) return err.message
    if (err && typeof err === 'object' && 'message' in err) {
      const msg = String((err as { message?: unknown }).message || '').trim()
      if (msg) return msg
    }
    return fallback
  }

  const handleSaveDraft = async () => {
    setError(null)
    try {
      await persistDraft()
      onClose()
    } catch (err) {
      suppressReinitRef.current = false
      setError(errorMessage(err, 'Erro ao salvar o contrato.'))
    }
  }

  const handleConclude = async () => {
    setError(null)
    let persisted = false
    try {
      const saved = await persistDraft()
      persisted = true
      try {
        await onConclude(saved)
      } catch (err) {
        // Rascunho já persistido; cancelar confirmação mantém o formulário aberto.
        if (isConfirmCancelled(err)) return
        throw err
      }
      onClose()
    } catch (err) {
      if (!persisted) suppressReinitRef.current = false
      setError(errorMessage(err, 'Erro ao concluir o contrato.'))
    }
  }

  const handleOpenAttachment = async (filePath: string) => {
    const url = await contratosService.getAttachmentUrl(filePath)
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
    else window.alert('Não foi possível abrir o anexo.')
  }

  const handleDeleteAttachment = async (id: string, fileName: string) => {
    if (!editing) return
    if (!window.confirm(`Excluir o anexo "${fileName}"?`)) return
    setDeletingAttachmentId(id)
    setError(null)
    try {
      await contratosService.softDeleteAttachment(id, {
        deleted_by: user?.id,
        deleted_reason: 'Exclusão pela tela de contratos',
      })
      void logDelete('contract_attachment', id, `Excluiu anexo do contrato ${editing.name}`, {
        file_name: fileName,
      })
      setSavedAttachments((prev) => prev.filter((att) => att.id !== id))
      const refreshed = await contratosService.getById(editing.id)
      if (refreshed) {
        setSavedAttachments(refreshed.attachments || [])
        onEditingChange?.(refreshed)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir anexo.')
    } finally {
      setDeletingAttachmentId(null)
    }
  }

  if (!open) return null

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={busy ? undefined : onClose} />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
              {editing ? 'Editar rascunho — Contrato' : 'Novo contrato'}
            </Heading>
            {(step === 'form' || step === 'payment') && (
              <Text size="sm" color="muted" className="mt-1">
                Preencha os dados. Na revisão você pode salvar o rascunho ou concluir.
              </Text>
            )}
            {step === 'review' && (
              <Text size="sm" color="muted" className="mt-1">
                Revise os dados. Salve como rascunho ou conclua o contrato.
              </Text>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 dark:border-gray-700 dark:bg-gray-900/40">
            <ContractTimeline steps={TIMELINE_STEPS} currentStep={currentTimelineStep} />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          {loadingMeta ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : step === 'form' ? (
            <div className="space-y-5">
              <SolicitacaoPickerField
                label="Solicitação vinculada (opcional)"
                requests={requests}
                value={requestId}
                onChange={setRequestId}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Título <span className="text-red-500">*</span>
                  </label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Número do contrato
                  </label>
                  <Input
                    value={contractNumber}
                    onChange={(e) => setContractNumber(e.target.value)}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Nome do contato
                  </label>
                  <Input
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Telefone
                  </label>
                  <Input
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    placeholder="Opcional"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Responsável
                  </label>
                  {!marketingDepartmentConfigured ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                      Configure o parâmetro departamento_marketing em Marketing &gt; Parâmetros.
                    </div>
                  ) : (
                    <select
                      value={responsibleId}
                      onChange={(e) => setResponsibleId(e.target.value)}
                      className={`w-full ${SELECT_CLASS}`}
                    >
                      <option value="">Selecione</option>
                      {responsibleOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name || p.id}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Descrição
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className={TEXTAREA_CLASS}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Fornecedor <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={supplierId}
                      onChange={(e) => setSupplierId(e.target.value)}
                      className={`min-w-0 flex-1 ${SELECT_CLASS}`}
                      required
                    >
                      <option value="">Selecione</option>
                      {fornecedores.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="inline-flex shrink-0 items-center gap-1"
                      onClick={() => setFornecedorCreateOpen(true)}
                      title="Cadastrar novo fornecedor"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Novo
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Fonte Pagadora <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={paymentSourceId}
                    onChange={(e) => setPaymentSourceId(e.target.value)}
                    className={`w-full ${SELECT_CLASS}`}
                    required
                  >
                    <option value="">Selecione</option>
                    {fontesPagadoras.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Departamento
                  </label>
                  <select
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    className={`w-full ${SELECT_CLASS}`}
                  >
                    <option value="">Selecione</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.display_name || d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Campanha
                  </label>
                  <select
                    value={campaignId}
                    onChange={(e) => setCampaignId(e.target.value)}
                    className={`w-full ${SELECT_CLASS}`}
                  >
                    <option value="">Nenhuma</option>
                    {activeCampaigns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Evento
                  </label>
                  <select
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    className={`w-full ${SELECT_CLASS}`}
                  >
                    <option value="">Nenhum</option>
                    {activeEvents.map((ev) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Adesão
                  </label>
                  <Input
                    type="date"
                    value={adhesionDate}
                    onChange={(e) => setAdhesionDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Tipo de renovação
                  </label>
                  <select
                    value={renewalType}
                    onChange={(e) =>
                      setRenewalType(e.target.value as '' | ContractRenewalType)
                    }
                    className={`w-full ${SELECT_CLASS}`}
                  >
                    <option value="">Selecione</option>
                    <option value="recorrente">Recorrente</option>
                    <option value="aditivo">Renovação</option>
                  </select>
                  {renewalType === 'recorrente' && (
                    <Text size="xs" color="muted" className="mt-1">
                      Contratos recorrentes renovam pela data de adesão.
                    </Text>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Data de término
                  </label>
                  <Input
                    type="date"
                    value={terminationDate}
                    onChange={(e) => setTerminationDate(e.target.value)}
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  Ativo
                </label>
              </div>

              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <Heading as="h4" size="lg" className="text-gray-900 dark:text-white">
                    Serviços
                  </Heading>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addService}
                    className="inline-flex items-center gap-1"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Adicionar serviço
                  </Button>
                </div>
                {editing &&
                  (editing.amounts || []).some((a) => Boolean(a.effective_to)) && (
                    <ul className="mb-3 space-y-2">
                      {(editing.amounts || [])
                        .filter((a) => Boolean(a.effective_to))
                        .map((a) => (
                          <li
                            key={a.id}
                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 dark:border-gray-600 dark:text-gray-300"
                          >
                            {formatCurrency(Number(a.amount))}
                            {a.notes ? ` — ${a.notes}` : ''}
                            {a.department
                              ? ` · ${a.department.display_name || a.department.name}`
                              : ''}{' '}
                            — {formatDateOnlyPtBr(a.effective_from)}
                            {a.effective_to
                              ? ` até ${formatDateOnlyPtBr(a.effective_to)}`
                              : ''}
                            <span className="ml-2 text-xs text-gray-400">(histórico)</span>
                          </li>
                        ))}
                    </ul>
                  )}
                <Text size="xs" color="muted" className="mb-3">
                  Serviços vigentes abaixo. Históricos com fim de vigência são preservados ao
                  salvar.
                </Text>
                <div className="space-y-3">
                  {services.map((row, index) => (
                    <div
                      key={index}
                      className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40"
                    >
                      <div className="mb-2 flex items-center justify-between">
                        <Text size="sm" className="font-medium text-gray-700 dark:text-gray-200">
                          Serviço {index + 1}
                        </Text>
                        <button
                          type="button"
                          onClick={() => removeService(index)}
                          disabled={services.length <= 1}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40 dark:hover:bg-red-900/20"
                          title="Remover serviço"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                            Valor <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={row.amount}
                            onChange={(e) => updateService(index, { amount: e.target.value })}
                            placeholder="0,00"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                            Departamento
                          </label>
                          <select
                            value={row.department_id}
                            onChange={(e) =>
                              updateService(index, { department_id: e.target.value })
                            }
                            className={`w-full ${SELECT_CLASS}`}
                          >
                            <option value="">Selecione</option>
                            {departments.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.display_name || d.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                            Início vigência <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="date"
                            value={row.effective_from}
                            onChange={(e) =>
                              updateService(index, { effective_from: e.target.value })
                            }
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                            Fim vigência
                          </label>
                          <Input
                            type="date"
                            value={row.effective_to}
                            onChange={(e) =>
                              updateService(index, { effective_to: e.target.value })
                            }
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                            Descrição do serviço
                          </label>
                          <Input
                            value={row.notes}
                            onChange={(e) => updateService(index, { notes: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Text size="xs" color="muted" className="mt-2">
                  Total do contrato: {formatCurrency(contractTotal)}
                </Text>
              </div>

              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <Heading as="h4" size="lg" className="mb-3 text-gray-900 dark:text-white">
                  Anexos
                </Heading>
                {savedAttachments.length > 0 && (
                  <ul className="mb-3 space-y-2">
                    {savedAttachments.map((att) => (
                      <li
                        key={att.id}
                        className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600"
                      >
                        <span className="truncate text-gray-700 dark:text-gray-200">
                          {att.file_name}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => void handleOpenAttachment(att.file_path)}
                            className={ICON_BTN_CLASS}
                            title="Abrir"
                            disabled={busy || deletingAttachmentId === att.id}
                          >
                            <DocumentArrowDownIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDeleteAttachment(att.id, att.file_name)}
                            className={ICON_BTN_DANGER_CLASS}
                            title="Excluir"
                            disabled={busy || deletingAttachmentId === att.id}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {pendingFiles.length > 0 && (
                  <ul className="mb-3 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                    {pendingFiles.map((f) => (
                      <li
                        key={`${f.name}-${f.size}`}
                        className="flex items-center justify-between gap-2"
                      >
                        <span>{f.name}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setPendingFiles((prev) =>
                              prev.filter((p) => !(p.name === f.name && p.size === f.size))
                            )
                          }
                          className="rounded p-1.5 text-gray-500 hover:bg-red-50 hover:text-red-600"
                          title="Remover"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <input
                  type="file"
                  accept={CONTRACT_ATTACHMENT_ACCEPT}
                  multiple
                  onChange={(e) => {
                    const list = Array.from(e.target.files || [])
                    if (list.length) setPendingFiles((prev) => [...prev, ...list])
                    e.target.value = ''
                  }}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 dark:text-gray-300 dark:file:bg-primary-900/30 dark:file:text-primary-300"
                />
              </div>
            </div>
          ) : step === 'review' ? (
            <div className="space-y-5">
              <Heading as="h4" size="lg" className="text-gray-900 dark:text-white">
                Revisão do contrato
              </Heading>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Text size="xs" color="muted">
                    Título
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {name.trim() || '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Número
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {contractNumber.trim() || '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Nome do contato
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {contactName.trim() || '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Telefone
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {contactPhone.trim() || '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Email
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {contactEmail.trim() || '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Fornecedor
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {supplierName}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Fonte pagadora
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {paymentSourceName}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Departamento
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {departmentName}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Responsável
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {responsibleName}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Solicitação
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {requestLabel}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Campanha
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {campaignName}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Evento
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {eventName}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Adesão
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {adhesionDate ? formatDateOnlyPtBr(adhesionDate) : '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Tipo de renovação
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {renewalType ? RENEWAL_LABELS[renewalType] : '—'}
                  </Text>
                </div>
                <div>
                  <Text size="xs" color="muted">
                    Término
                  </Text>
                  <Text size="sm" className="font-medium text-gray-900 dark:text-white">
                    {terminationDate ? formatDateOnlyPtBr(terminationDate) : '—'}
                  </Text>
                </div>
              </div>

              {description.trim() && (
                <div>
                  <Text size="xs" color="muted">
                    Descrição
                  </Text>
                  <Text size="sm" className="whitespace-pre-wrap text-gray-900 dark:text-white">
                    {description.trim()}
                  </Text>
                </div>
              )}

              <div>
                <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Serviços · {formatCurrency(contractTotal)}
                </Text>
                <ul className="space-y-2">
                  {services.map((row, index) => {
                    const amount = Number(String(row.amount).replace(',', '.'))
                    return (
                      <li
                        key={`svc-review-${index}`}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700"
                      >
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(Number.isFinite(amount) ? amount : 0)}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">
                          {' '}
                          · {formatDateOnlyPtBr(row.effective_from)}
                          {row.effective_to
                            ? ` até ${formatDateOnlyPtBr(row.effective_to)}`
                            : ''}
                        </span>
                        {row.notes ? (
                          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                            {row.notes}
                          </span>
                        ) : null}
                      </li>
                    )
                  })}
                </ul>
              </div>

              <div>
                <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                  Formas de pagamento
                </Text>
                <ul className="space-y-2">
                  {paymentGroups.map((group, gIndex) => {
                    const methodName =
                      paymentMethods.find((m) => m.id === group.payment_method_id)?.name ||
                      'Forma não selecionada'
                    const groupTotal = group.installments.reduce(
                      (acc, row) => acc + (Number(row.amount) || 0),
                      0
                    )
                    return (
                      <li
                        key={`pay-review-${gIndex}`}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700"
                      >
                        <p className="font-medium text-gray-900 dark:text-white">
                          {methodName} · {formatCurrency(groupTotal)}
                        </p>
                        <ul className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {group.installments.map((inst, iIndex) => (
                            <li key={`pay-inst-${gIndex}-${iIndex}`}>
                              {iIndex + 1}ª · {formatCurrency(Number(inst.amount) || 0)} ·{' '}
                              {inst.due_date ? formatDateOnlyPtBr(inst.due_date) : '—'}
                            </li>
                          ))}
                        </ul>
                      </li>
                    )
                  })}
                </ul>
              </div>

              <div>
                <Text size="sm" className="font-semibold text-gray-900 dark:text-white">
                  Anexos: {savedAttachments.length + pendingFiles.length}
                </Text>
                {(savedAttachments.length > 0 || pendingFiles.length > 0) && (
                  <ul className="mt-1 space-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                    {savedAttachments.map((att) => (
                      <li key={att.id}>{att.file_name}</li>
                    ))}
                    {pendingFiles.map((file, index) => (
                      <li key={`pending-${index}`}>{file.name} (novo)</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : step === 'payment' ? (
            <NotasPaymentStep
              groups={paymentGroups}
              methods={paymentMethods}
              invoiceTotal={contractTotal}
              issueDate={adhesionDate || firstServiceFrom}
              onChange={setPaymentGroups}
            />
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            {step !== 'form' && (
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setError(null)
                  if (step === 'review') setStep('payment')
                  else setStep('form')
                }}
              >
                Voltar
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
              Cancelar
            </Button>
            {step === 'form' && (
              <Button type="button" variant="solid" disabled={busy} onClick={goToPayment}>
                Continuar para pagamento
              </Button>
            )}
            {step === 'payment' && (
              <Button type="button" variant="solid" disabled={busy} onClick={goToReview}>
                Continuar
              </Button>
            )}
            {step === 'review' && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void handleSaveDraft()}
                >
                  {busy ? 'Salvando...' : 'Salvar contrato'}
                </Button>
                <Button
                  type="button"
                  variant="solid"
                  disabled={busy}
                  onClick={() => void handleConclude()}
                >
                  {busy ? 'Concluindo...' : 'Concluir'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    <EstoqueFornecedorCreateModal
      open={fornecedorCreateOpen}
      onClose={() => setFornecedorCreateOpen(false)}
      onCreated={(created) => {
        setFornecedores((prev) =>
          [...prev.filter((f) => f.id !== created.id), created].sort((a, b) =>
            a.name.localeCompare(b.name, 'pt-BR')
          )
        )
        setSupplierId(created.id)
      }}
    />
    </>
  )
}

export default ContratosFormModal
